import { startOfHour } from 'date-fns';
import format from 'date-fns-tz/format';
import parseISO from 'date-fns/parseISO';
import { ENV_ALPHA } from 'environments/i-alpha';
import { ENV_BETA } from 'environments/i-beta';
import { ENV_DEV } from 'environments/i-dev';
import { ENV_LOCAL } from 'environments/i-local';
import { ENV_MAINTENANCE } from 'environments/i-maintenance';
import { ENV_NGROK } from 'environments/i-ngrok';
import { ENV_PROD } from 'environments/i-prod';
import { ENV_PROD_JP } from 'environments/i-prod-jp';
import { ENV_PROD_KR } from 'environments/i-prod-kr';
import { ENV_SANDBOX } from 'environments/i-sandbox';
import { ENV_STAGING } from 'environments/i-staging';
import { IEnvConfig } from 'interfaces/i-env-config';
import {
  LOCAL_DATETIME_FORMAT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { EnvIdentifier } from 'lib_ts/enums/env.enums';
import {
  buildVersion,
  IBuildMetadata,
} from 'lib_ts/interfaces/common/i-build-metadata';

const BUILD_METADATA = (() => {
  try {
    return {
      // Static paths so webpack can bundle these JSON files
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      ...require('./build-metadata.json'),
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      ...require('./build-timestamp.json'),
    } as IBuildMetadata;
  } catch (e) {
    console.error(e);

    return {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      ...require('./build-metadata.json'),
      updated: new Date().toISOString(),
    } as IBuildMetadata;
  }
})();

const BUILD_VERSION = buildVersion(BUILD_METADATA);

// like "4.7.0.1-RC1" => [4,7,0,1]
export const BUILD_VERSION_PARTS = BUILD_VERSION.split('.')
  .filter((_, i) => i < 4)
  .map((m) => parseInt(m));

const BUILD_UPDATED = parseISO(BUILD_METADATA.updated);
const BUILD_UPDATED_DISPLAY = format(
  startOfHour(BUILD_UPDATED),
  LOCAL_DATETIME_FORMAT,
  { timeZone: LOCAL_TIMEZONE }
);

/** avoids crashing the app if no file is found */
const localEnvConfig = (): Partial<IEnvConfig> => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require('./local.config.json');
  } catch {
    return {};
  }
};

/** based on target REACT_APP_STAGE from build command */
const mainEnvConfig = (): IEnvConfig => {
  switch (process.env.REACT_APP_STAGE as EnvIdentifier) {
    case 'prod':
      return ENV_PROD;

    case 'prod-jp':
      return ENV_PROD_JP;

    case 'prod-kr':
      return ENV_PROD_KR;

    case 'staging':
      return ENV_STAGING;

    case 'sandbox':
      return ENV_SANDBOX;

    case 'dev':
      return ENV_DEV;

    case 'maintenance':
      return ENV_MAINTENANCE;

    case 'alpha': {
      return ENV_ALPHA;
    }

    case 'beta': {
      return ENV_BETA;
    }

    case 'ngrok': {
      return ENV_NGROK;
    }

    default:
      return ENV_LOCAL;
  }
};

const config: IEnvConfig = {
  ...mainEnvConfig(),
  ...localEnvConfig(),
};

interface IConfig extends IEnvConfig {
  version: string;
  updated: Date;

  /** for displaying version and updated timestamp as string */
  getBuildStamp: () => string;

  safetyPolicyRevisionDate: string;
}

const output: IConfig = {
  version: BUILD_VERSION,
  updated: BUILD_UPDATED,
  safetyPolicyRevisionDate: '2024-01-30',

  getBuildStamp: () => `v${BUILD_VERSION} @ ${BUILD_UPDATED_DISPLAY}`,

  /** export conditional config */
  ...config,
};

export default output;
